import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useCreditNote, { useCreditNoteForm } from '@hooks/useCreditNote'

import { ActionResult } from '@redux/modules/module'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { CreditNoteModel } from '@models/types'

import CreditNoteItems from './components/CreditNoteItems'

const MODAL_KEY = 'CreateOrEditCreditNoteModal'

export type ModalCallbacks = {
  closeAllModals: () => void,
  createCreditNote: (payload: HandleCallbackActionParams) => void,
  createCreditNoteItem: (payload: HandleCallbackActionParams) => void,
  updateCreditNote: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditCreditNoteModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const {
    closeAllModals, createCreditNote, updateCreditNote,
  } = callbacks

  const modalContext = useContext(ModalContext)
  const {
    callbacks: { setModalData },
  } = modalContext

  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const initCreditNote = digObject(modalPayload, 'creditNote')

  const creditNotePayload = useCreditNote(initCreditNote)
  const {
    callbacks: { createCreditNote: createFn, updateCreditNote: updateFn },
    creditNote,
    creating,
    updating,
  } = creditNotePayload

  const newRecord = !creditNote.id

  const creditNoteForm = useCreditNoteForm(creditNote, { validateOn: 'change' })
  const {
    entityState,
    entityState: {
      contact_name,
      notes,
      reference,
    },
    handlers,
    saveEnabled,
  } = creditNoteForm

  const createOrEditCreditNotePayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: ({ response }: { response: ActionResult<CreditNoteModel> }) => {
        if (!newRecord) return closeAllModals()
        // @ts-expect-error TS2339
        setModalData('CreateOrEditCreditNoteModal', { creditNote: response?.data })
      },
    },
    entityParams: entityState,
    requestOptions: { include_credit_note_items: true },
    toastText: `Credit Note ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    // @ts-expect-error TS2741
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      {/*
       // @ts-expect-error TS2559 */}
      <SidebarModal.Header callbacks={callbacks} title={newRecord ? 'Create' : 'Edit'} titleSecondLine="Credit Note" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="Contact Name">
            <input
              name="contact_name"
              placeholder="Leave blank for default"
              type="text"
              value={contact_name}
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="Reference" marginTop="large">
            <input name="reference" placeholder="Leave blank for default" type="text" value={reference} {...handlers} />
          </Form.Field>

          <Form.Field label="Notes" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea name="notes" style={{ height: 80, resize: 'vertical' }} value={notes} {...handlers} />
          </Form.Field>

          {/*
           // @ts-expect-error TS2739 */}
          <CreditNoteItems callbacks={callbacks} creditNote={creditNote} newRecord={newRecord} />
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              // @ts-expect-error TS2345
              ? () => createCreditNote(createOrEditCreditNotePayload)
              // @ts-expect-error TS2345
              : () => updateCreditNote(createOrEditCreditNotePayload)
          }
          size="large"
        >
          {newRecord ? 'Create Credit Note' : 'Save Credit Note'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditCreditNoteModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
